import { useState } from 'react'

import { ActionIcon, Menu, Tooltip } from '@mantine/core'
import { Smile } from 'lucide-react'

import StyledMenu from 'src/components/Overlays/StyledMenu/StyledMenu'

import { EMOJI_GROUPS } from './emojis'

interface EmojiPickerProps {
  /** Called with the chosen emoji character, e.g. '👍'. */
  onSelect: (emoji: string) => void
  disabled?: boolean
  /** Tooltip and aria-label for the trigger. */
  label?: string
  /** Dropdown placement — composers sit at the bottom, so open upwards. */
  position?: 'top-end' | 'top-start' | 'bottom-end' | 'bottom-start'
}

const EmojiPicker = ({
  onSelect,
  disabled = false,
  label = 'Add emoji',
  position = 'top-end',
}: EmojiPickerProps) => {
  const [opened, setOpened] = useState(false)

  const handleSelect = (emoji: string) => {
    onSelect(emoji)
    setOpened(false)
  }

  return (
    <StyledMenu
      width={264}
      position={position}
      opened={opened}
      onChange={setOpened}
      middlewares={{ flip: true, shift: true }}
    >
      <Menu.Target>
        <Tooltip label={label} position="top" className="text-xs">
          <ActionIcon
            variant="subtle"
            radius="md"
            aria-label={label}
            disabled={disabled}
            // Keep focus in the composer so the caret we insert at survives the
            // click. preventDefault on mousedown suppresses the blur without
            // suppressing the click.
            onMouseDown={(event) => event.preventDefault()}
            className="transition-colors hover:bg-doubleNickel-gray-100"
          >
            <Smile
              size={18}
              className="fill-none stroke-doubleNickel-gray-500"
            />
          </ActionIcon>
        </Tooltip>
      </Menu.Target>

      <Menu.Dropdown className="p-2">
        <div className="flex flex-col gap-2">
          {EMOJI_GROUPS.map((group) => (
            <div key={group.label} className="flex flex-col gap-1">
              <span className="text-[10px] font-medium uppercase tracking-wide text-doubleNickel-gray-400">
                {group.label}
              </span>
              <div className="flex flex-row flex-wrap gap-0.5">
                {group.emojis.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    // Screen readers announce the emoji's own Unicode name, so
                    // the character is a better label than a hand-written one.
                    aria-label={emoji}
                    onClick={() => handleSelect(emoji)}
                    onMouseDown={(event) => event.preventDefault()}
                    className="flex h-7 w-7 items-center justify-center rounded text-lg leading-none transition-colors hover:bg-doubleNickel-brand-25"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Menu.Dropdown>
    </StyledMenu>
  )
}

export default EmojiPicker
